import { ISignatureStore } from "@/stores/signatureStore_I";
import { ResourceOption, ResourceType, SelectedResource } from "./SelectedFocusResources";
import { FocusDTO, FocusTabDTO } from "@/js/api/focus";
import { EditingFocus } from "../FocusPage";
import { isLockedFocusCompatibleUrl } from "@/js/utils/urlUtils";

export function getSignaturesForFocusResourcePicker(signatureSlice: ISignatureStore): ResourceOption[] {
	const out = [];

	const sortedSignatures = signatureSlice.data.signatures.sort(function (a, b) {
		return a.name.localeCompare(b.name);
	});

	for (const signature of sortedSignatures) {
		if (
			signature.enabled &&
			signature.focus &&
			signature.url &&
			signature.url.toLowerCase() !== "none" &&
			signature.id !== "sphirewall.application.porn" &&
			signature.id !== "sphirewall.application.childporn" &&
			signature.id !== "sphirewall.application.offensive"
		) {
			out.push({ label: signature.name, resourceType: ResourceType.Signature, resourceValue: signature.id });
		}
	}
	return out;
}

/**
 * Parses the given URL string and returns the domain.
 * @param urlString The URL string to parse.
 * @returns The domain if the URL is valid, otherwise null.
 */
export function parseDomain(urlString: string): string | null {
	// Early validation for invalid characters and patterns
	if (!urlString || typeof urlString !== "string") {
		return null;
	}

	// Check for spaces, which are invalid in URLs
	if (urlString.includes(" ") || urlString.includes("\n") || urlString.includes("\r") || urlString.includes("\t")) {
		return null;
	}

	try {
		// If URL doesn't have a protocol, prepend one to make it a valid URL before instantiating URL object.
		if (!urlString.includes("://")) {
			urlString = `https://${urlString}`;
		}

		const url = new URL(urlString); // This will throw a TypeError if the URL is invalid.

		// Decode it so wildcards are not "%2A"
		const parts = decodeURIComponent(url.hostname).split(".");

		if (parts[0] === "www") {
			parts.shift();
		}

		if (parts.length < 2) {
			throw new Error("A domain cannot be a single word");
		}

		for (let i = 0; i < parts.length; i++) {
			if (!parts[i].includes("*")) {
				continue;
			}
			if (parts[i].length > 1 || i !== 0) {
				throw new Error("An asterisk can only be used in the first part of the domain and must be the only character in the subdomain");
			}
		}

		const tld = parts[parts.length - 1];
		if (tld.length < 2 && tld !== "*") {
			throw new Error("A domain cannot have a TLD that is less than 2 characters unless it's a wildcard");
		}

		return parts.join(".");
	} catch (error) {
		console.error("Error parsing domain", error);
		return null;
	}
}

export function transformFocusTabDTOToResourceOption(focusResource: FocusTabDTO): ResourceOption {
	const resourceType: ResourceType = focusResource.url ? ResourceType.Url : ResourceType.Signature;
	const resourceValue = resourceType === ResourceType.Url ? focusResource.url : focusResource.signature;

	return {
		label: "LABEL",
		resourceType: resourceType,
		resourceValue: resourceValue || "",
	};
}

/**
 * This utility function is useful for transforming FocusTabDTO objects that
 * come from CPS when fetching resources a Focus uses, into SelectedResource
 * objects to be used in the SelectedFocusTabDTOs multiselect component.
 *
 * This is not a DTO safe to be sent back to the server as it does not match the API contract. It must be transformed back into a FocusTabDTO object first.
 *
 * @param focusResource The FocusTabDTO object to transform.
 * @param allSignatureResourceOptions All the signature resources available, used to grab the label if a signature.
 */
export function transformFocusTabDTOToSelectedResource(
	focusResource: FocusTabDTO,
	allSignatureResourceOptions: ResourceOption[]
): SelectedResource {
	const resourceType: ResourceType = focusResource.url ? ResourceType.Url : ResourceType.Signature;
	const resourceValue = resourceType === ResourceType.Url ? focusResource.url : focusResource.signature;

	const label =
		resourceType === ResourceType.Signature
			? allSignatureResourceOptions.find((x) => x.resourceValue === resourceValue)?.label
			: `Website: ${resourceValue || ""}`;

	const isLockedFocusCompatible = resourceType === ResourceType.Signature ? false : isLockedFocusCompatibleUrl(resourceValue);

	return {
		id: focusResource.id,
		resourceType: resourceType,
		resourceValue: resourceValue || "",
		openTab: focusResource.openTab,
		label: label || "",
		isValid: true,
		isLockedFocusCompatible: isLockedFocusCompatible,
	};
}

export function transformFocusDtoToEditingFocus(focus: FocusDTO, allSignatureResourceOptions: ResourceOption[]): EditingFocus {
	const selectedResources = focus.focusTabs.map((resource) =>
		transformFocusTabDTOToSelectedResource(resource, allSignatureResourceOptions)
	);

	return {
		id: focus.id,
		name: focus.name,
		active: focus.active,
		resources: selectedResources,
		isOnTheFly: !focus.name, // If there is no name, it's an on-the-fly focus.
		tabsLocked: focus.tabsLocked,
		studentIds: focus.studentIds || [],
	};
}

export function searchSignatures(signatures: ResourceOption[], searchTerm?: string, maxResults = 10): ResourceOption[] {
	const searchTermLower = searchTerm?.toLowerCase() || "";

	const filteredSignatures = signatures.filter((sig) => {
		const labelTextLower = sig.label.toLowerCase();
		if (searchTermLower.length === 0) return true; // Show all items if search term is empty
		if (searchTermLower.length <= 2 && labelTextLower.startsWith(searchTermLower)) return true; // Partial match for short search terms
		if (searchTermLower.length > 2 && labelTextLower.includes(searchTermLower)) return true; // Full match for longer search terms
		return false;
	});

	if (filteredSignatures.length > maxResults) {
		filteredSignatures.length = maxResults;
	}

	return filteredSignatures;
}
